// HomeFormMobile.tsx
// see HomeForm.tsx
import React, { useState, useEffect, use, useMemo } from 'react';
import { Button, Box, Typography, Paper, ButtonBase, Stack, IconButton, CircularProgress } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import Grid from '@mui/material/Grid2';
import { Link, useNavigate } from 'react-router-dom';
import { useFetch } from '../../../hooks/useFetch3';
import { api } from "../../../api/plateApi";
import { api as api2 } from "../../../api/plateApi2";
import { api as groupApi } from "../../../api/groupApi";
import { api as eventApi } from "../../../api/eventApi";
import { api as reactionApi } from "../../../api/reactionApi";
import { Group } from '../../../types/group';
import { EntityPath, Plate } from '../../../types/plate';
import PlateForm from './PlateForm3';
import toast, { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { EventImage, EventRecord } from '../../../types/event';
import { EventShortList } from './EventShortListMobile';
import DoublePaper from './DoublePaper';
import { ReactionType } from '../../../types/reaction';
import { formatDateTime } from '../../../tools/util';
import { useAuthStore } from '../../../store/useAuthStore3';
import { Subtenant, subtenantApi } from '../../../api/subtenantApi';

const HomeForm: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { customerId } = useAuthStore();

    if (!customerId) {
        return <Typography>t('customer.loading')</Typography>;
    }

// plate section

    const [ isOtp, setIsOtp] = useState<boolean>(false);
    const { data: groups, loading: loadingGroups} = useFetch<Group[]>(groupApi.get, []);
    const { data: entityPaths, } = useFetch<EntityPath[]>(api.getEntityPaths, []);
    const { data: reactionTypes } = useFetch<ReactionType[]>(reactionApi.getReactionType, []);
    const { data: subtenants } = useFetch<Subtenant[]>(subtenantApi.get, []);
    const [formKey, setFormKey] = useState(0);

    const emptyPlate: Plate = useMemo(() => ({
        id: null,
        customer_id: customerId,
        group_id: groups.length > 0 ? groups[0].group_id : -1,
        registration_number: '',
        valid_since: new Date().toISOString().split('T')[0],
        valid_to: '2099-12-31',
        car_owner: '',
        car_brand: '',
        is_otp: false,
        is_enabled: true,
        reactions: [],
    }), [groups, customerId]);
    
    const [plate, setPlate] = useState<Plate>(emptyPlate);
    const [version, setVersion] = useState(0);

    useEffect(() => {
        if (groups.length > 0 && plate.group_id === -1) {
            setPlate({ ...emptyPlate }); // Создаем новый объект с теми же данными    
        }
    }, [groups, emptyPlate]);

    const refreshEventShortList = () => setVersion(v => v + 1);

    const handlePlateSave = async (dataEdited: Plate) => {
        const newData = await api.postAndSynchronize(dataEdited);
        setPlate({ ...emptyPlate }); // Создаем новый объект с теми же данными    
        setFormKey(prev => prev + 1); // Форма перерисуется "с нуля"
        toast.success(t('common.saved_successfully'));
    };

    const handlePlateSave2 = async (dataEdited: Plate) => {
        const res = await api2.upsertPlate(dataEdited);
        setPlate({ ...emptyPlate }); // Создаем новый объект с теми же данными    
        setFormKey(prev => prev + 1); // Форма перерисуется "с нуля"
        toast.success(t('common.saved_successfully'));
    };

    const handlePlateCancel = async () =>{
        // (Опционально) маленькая пауза, чтобы UI не "прыгал" мгновенно
        // await new Promise(r => setTimeout(r, 200));

        setPlate({ ...emptyPlate }); // Создаем новый объект с теми же данными    
        setFormKey(prev => prev + 1); // Форма перерисуется "с нуля"
        toast.error(t('common.Cancelld'));
    }

    const {data: imageData, refresh: refreshImageData, loading: imageLoading }  = useFetch<EventImage>(eventApi.getLastImage, { 
        object: '', 
        background: '', 
        origin_name: '', 
        result: false,
        ts_start: '2000-01-01'
    });
    const backgroundSrc = `data:image/jpeg;base64,${imageData?.background}`;

    const handleRefresh = () => {
        refreshEventShortList();
        refreshImageData();
    };

    return (
        <Box sx={{ p: 0, with: '100%' }}>
            <Toaster />

            <DoublePaper w1={3.4} w2={8}>
                {/* <div>Я занимаю ...% ширины</div> */}
                {/* <div>Я занимаю ...% ширины</div> */}
                    <PlateForm
                        key={formKey}
                        plate={plate}
                        subtitle={t('Access_pass')}
                        groups={groups}
                        subtenants={subtenants}
                        entityPaths={entityPaths}
                        onSave={handlePlateSave2}
                        onCancel={handlePlateCancel}
                        reactionTypes={reactionTypes}
                    />
                    <div>
                    <Stack direction='row' spacing={2} sx={{ alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">{t('HomeForm.LastEvent')}</Typography>
                        <Typography variant="subtitle1" color="primary">{imageData.origin_name}</Typography>
                        <Typography variant="subtitle1" color="primary">{formatDateTime(imageData.ts_start)}</Typography>
                        <Box sx={{ flexGrow: 1 }} />
                        <IconButton onClick={handleRefresh}>
                            <RefreshIcon />
                        </IconButton>
                    </Stack>
                    
                    {imageLoading ? (
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center',
                            height: 200 
                        }}>
                            <CircularProgress />
                        </Box>
                    ) : imageData.result && (
                        <Box sx={{ 
                            flex: 1,          // Занимает всё оставшееся место в Paper
                            width: '100%', 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center',
                            overflow: 'hidden'
                        }}>
                            <img 
                                src={backgroundSrc} 
                                alt="Object background" 
                                style={{ 
                                    width: '100%', 
                                    height: '100%', 
                                    // objectFit: 'contain', // Сохраняет пропорции без обрезки
                                    objectFit: 'cover',
                                    borderRadius: '4px'
                                }}
                            />
                        </Box>
                    )}
                        
                    </div>

            </DoublePaper>
            
            <br/>
            
            {/* PAGE 3 */}
            <Paper elevation={3} sx={{ width: '100%', p: 3 }}>
                <EventShortList key={version}/>
            </Paper>
        </Box>
    );
};

export default HomeForm;